import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  Building2,
  Loader2,
  MessageSquare,
  Phone,
  Pencil,
  Star,
  Trash2,
  User,
} from 'lucide-react';
import {
  useCompanyPhoneNumberMutations,
  useCompanyPhoneNumbers,
  useTwilioAccountNumbers,
  useUserNumberAssignments,
  type CompanyPhoneNumber,
} from '@/hooks/useCompanyPhoneNumbers';

const BUSINESS_UNITS = [
  { value: 'none', label: 'Não definida' },
  { value: 'us', label: 'Estados Unidos' },
  { value: 'br', label: 'Brasil' },
];

const USAGE_TYPES = [
  { value: 'voice', label: 'Voz (ligações)' },
  { value: 'sms', label: 'SMS' },
  { value: 'voice_sms', label: 'Voz + SMS' },
];

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return value;
}

export default function CompanyPhoneNumbers() {
  const { data: companyNumbers = [], isLoading: loadingCompany } = useCompanyPhoneNumbers();
  const {
    data: twilioNumbers = [],
    isLoading: loadingTwilio,
    isError: twilioError,
  } = useTwilioAccountNumbers();
  const { data: assignments = [] } = useUserNumberAssignments();
  const { registerNumber, updateNumber, removeNumber, setMainNumber } =
    useCompanyPhoneNumberMutations();

  const [editing, setEditing] = useState<CompanyPhoneNumber | null>(null);
  const [form, setForm] = useState({
    label: '',
    description: '',
    business_unit: 'none',
    usage_type: 'voice',
  });

  const assignmentMap = useMemo(() => {
    const map = new Map<string, string>();
    assignments.forEach((a) => map.set(a.phone_number, a.user_name));
    return map;
  }, [assignments]);

  const companyMap = useMemo(() => {
    const map = new Map<string, CompanyPhoneNumber>();
    companyNumbers.forEach((n) => map.set(n.phone_number, n));
    return map;
  }, [companyNumbers]);

  const mainNumber = companyNumbers.find((n) => n.is_main) ?? null;

  const openEdit = (item: CompanyPhoneNumber) => {
    setEditing(item);
    setForm({
      label: item.label ?? '',
      description: item.description ?? '',
      business_unit: item.business_unit ?? 'none',
      usage_type: item.usage_type ?? 'voice',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    await updateNumber.mutateAsync({
      id: editing.id,
      label: form.label,
      description: form.description || null,
      business_unit: form.business_unit === 'none' ? null : form.business_unit,
      usage_type: form.usage_type,
    });
    setEditing(null);
  };

  return (
    <div className="container mx-auto py-6 max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Building2 className="h-7 w-7 text-primary" />
          Números da Empresa
        </h1>
        <p className="text-muted-foreground mt-2">
          Central de gerenciamento dos números Twilio corporativos — o número principal da empresa
          não pertence a nenhum colaborador.
        </p>
      </div>

      {/* Número principal */}
      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Star className="h-5 w-5 text-amber-500" />
            Número principal da empresa
          </CardTitle>
          <CardDescription>
            Usado como identificação de chamadas quando o colaborador não tem número próprio.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingCompany ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : mainNumber ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/40 p-4">
              <div>
                <p className="font-mono text-xl font-semibold">
                  {formatPhone(mainNumber.phone_number)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {mainNumber.label || 'Sem rótulo'}
                  {mainNumber.business_unit ? ` · ${mainNumber.business_unit.toUpperCase()}` : ''}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(mainNumber)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum número principal definido. Escolha um número corporativo na lista abaixo e
                clique em "Definir como principal".
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Quem atende o número da empresa */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quem atende o número da empresa</CardTitle>
          <CardDescription>
            Configuração própria deste canal (não altera a configuração de transferência de
            ligações). As chamadas recebidas no número da empresa tocam em grupo por níveis,
            separados por unidade (US/BR), com tempo de espera antes de escalar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <Link to="/settings/communication/company-call-ring">Configurar grupos de toque</Link>
          </Button>
        </CardContent>
      </Card>



      {/* Números corporativos registrados */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Números corporativos registrados</CardTitle>
          <CardDescription>
            Números da empresa, sem dono individual, disponíveis para o atendimento.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingCompany ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : companyNumbers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum número corporativo registrado ainda.
            </p>
          ) : (
            companyNumbers.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Phone className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">
                        {formatPhone(item.phone_number)}
                      </span>
                      {item.is_main && (
                        <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15">
                          <Star className="h-3 w-3 mr-1" />
                          Principal
                        </Badge>
                      )}
                      {!item.is_active && <Badge variant="secondary">Inativo</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {item.label || 'Sem rótulo'}
                      {item.business_unit ? ` · ${item.business_unit.toUpperCase()}` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!item.is_main && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMainNumber.mutate(item.id)}
                      disabled={setMainNumber.isPending}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      Definir como principal
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNumber.mutate(item.id)}
                    disabled={removeNumber.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Números da conta Twilio */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Números na conta Twilio</CardTitle>
          <CardDescription>
            Todos os números comprados na conta e para quem cada um está atribuído.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingTwilio ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : twilioError || twilioNumbers.length === 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Não foi possível carregar os números da conta Twilio. Verifique as credenciais da
                integração.
              </AlertDescription>
            </Alert>
          ) : (
            twilioNumbers.map((num) => {
              const assignedTo = assignmentMap.get(num.phoneNumber);
              const registered = companyMap.get(num.phoneNumber);
              const voice = num.capabilities?.voice;
              const sms = num.capabilities?.SMS ?? num.capabilities?.sms;

              return (
                <div
                  key={num.phoneNumber}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-medium">
                        {formatPhone(num.phoneNumber)}
                      </span>
                      {registered?.is_main && (
                        <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15">
                          <Star className="h-3 w-3 mr-1" />
                          Principal
                        </Badge>
                      )}
                      {assignedTo ? (
                        <Badge variant="secondary">
                          <User className="h-3 w-3 mr-1" />
                          {assignedTo}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          <Building2 className="h-3 w-3 mr-1" />
                          Sem dono
                        </Badge>
                      )}
                      {voice && (
                        <Badge variant="outline" className="text-xs">
                          <Phone className="h-3 w-3 mr-1" />
                          Voz
                        </Badge>
                      )}
                      {sms && (
                        <Badge variant="outline" className="text-xs">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          SMS
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {num.friendlyName || 'Sem nome no Twilio'}
                    </p>
                  </div>

                  <div>
                    {registered ? (
                      <Badge variant="secondary">Registrado na central</Badge>
                    ) : assignedTo ? (
                      <span className="text-xs text-muted-foreground">
                        Número pessoal — desatribua no perfil do usuário para usar como corporativo
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={registerNumber.isPending}
                        onClick={() =>
                          registerNumber.mutate({
                            phone_number: num.phoneNumber,
                            label: num.friendlyName || 'Número da empresa',
                          })
                        }
                      >
                        Registrar como número da empresa
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar número corporativo</DialogTitle>
            <DialogDescription>
              {editing ? formatPhone(editing.phone_number) : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="label">Rótulo</Label>
              <Input
                id="label"
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                placeholder="Ex.: Central de Atendimento"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Para que este número é usado"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Unidade de negócio</Label>
                <Select
                  value={form.business_unit}
                  onValueChange={(v) => setForm((p) => ({ ...p, business_unit: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_UNITS.map((bu) => (
                      <SelectItem key={bu.value} value={bu.value}>
                        {bu.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tipo de uso</Label>
                <Select
                  value={form.usage_type}
                  onValueChange={(v) => setForm((p) => ({ ...p, usage_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {USAGE_TYPES.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button onClick={saveEdit} disabled={updateNumber.isPending}>
              {updateNumber.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
